import { Link } from "react-router-dom";
import { Play, Pause } from "lucide-react";
import { toast } from "sonner";
import { usePlayer } from "@/context/PlayerContext";
import { fileUrl, FALLBACK_COVER } from "@/lib/api";

export const PlayButton = ({ release, trackIdx = 0, testid, className = "h-12 w-12" }) => {
  const { current, playing, playRelease, toggle } = usePlayer();
  const isThis = current && current.release_id === release.id && (trackIdx === null || current.id === release.tracks?.[trackIdx]?.id);
  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isThis) return toggle();
    if (!playRelease(release, trackIdx ?? 0)) toast("Nessun audio disponibile per questa uscita");
  };
  return (
    <button onClick={onClick} data-testid={testid} aria-label={isThis && playing ? "Pausa" : "Ascolta"}
      className={`${className} rounded-full bg-emerald-500 text-black grid place-items-center shadow-[0_0_24px_rgba(16,185,129,0.45)] hover:scale-110 transition-transform duration-200`}>
      {isThis && playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
    </button>
  );
};

export const ReleaseCard = ({ release, i = 0 }) => (
  <Link to={`/uscite/${release.id}`} data-testid={`release-card-${release.id}`} className="group block rise" style={{ animationDelay: `${i * 70}ms` }}>
    <div className="relative aspect-square overflow-hidden rounded-sm border border-white/10 bg-[#12141c] group-hover:border-emerald-500/50 group-hover:shadow-[0_0_30px_rgba(16,185,129,0.18)] transition-[border-color,box-shadow] duration-300">
      <img src={fileUrl(release.cover_url) || FALLBACK_COVER} alt={release.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      {release.catalog_number && (
        <span className="absolute top-3 left-3 font-mono-tga text-[10px] tracking-[0.2em] bg-black/70 backdrop-blur px-2 py-1 border border-white/10">{release.catalog_number}</span>
      )}
      <div className="absolute bottom-3 right-3 opacity-100 sm:opacity-0 sm:translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
        <PlayButton release={release} trackIdx={null} testid={`release-card-play-${release.id}`} />
      </div>
    </div>
    <div className="pt-4 space-y-1">
      <p className="font-mono-tga text-[10px] uppercase tracking-[0.25em] text-emerald-400">{release.release_type}{release.genre ? ` · ${release.genre}` : ""}</p>
      <h3 className="font-display text-base font-semibold uppercase tracking-tight leading-tight group-hover:text-emerald-300 transition-colors">{release.title}</h3>
      <p className="text-sm text-zinc-400">{release.artist_name}</p>
    </div>
  </Link>
);

export const EmptyState = ({ title, text, testid }) => (
  <div data-testid={testid} className="border border-dashed border-white/15 rounded-sm py-20 px-6 text-center">
    <div className="mx-auto h-20 w-20 rounded-full vinyl-grooves vinyl-spin border border-white/10 grid place-items-center mb-6">
      <span className="h-5 w-5 rounded-full bg-emerald-500" />
    </div>
    <p className="font-display uppercase text-lg">{title}</p>
    <p className="text-sm text-zinc-400 mt-2">{text}</p>
  </div>
);

export const PageTitle = ({ over, title, sub }) => (
  <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-12">
    <p className="font-mono-tga text-xs uppercase tracking-[0.3em] text-emerald-400 rise">{over}</p>
    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter mt-4 rise" style={{ animationDelay: "80ms" }}>{title}</h1>
    {sub && <p className="text-base md:text-lg text-zinc-400 mt-4 max-w-2xl rise" style={{ animationDelay: "160ms" }}>{sub}</p>}
  </div>
);
